import { useState, useEffect } from 'react';
import axios from 'axios';
import { Input } from '../ui/input';
import { Crown, Check, Star } from 'lucide-react';
import { API } from './constants';

/**
 * Plan Selection — final onboarding step.
 * Shows monthly / annual / lifetime plans + (for unregistered users) email & password
 * fields, plus a referral-code input that validates against the backend.
 */
export default function PlanSelection({
  displayName,
  yearsCoaching,
  plans,
  onSelectPlan,
  loading,
  isAuthenticated,
  email,
  setEmail,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  planPageConfig,
  onSignOut,
}) {
  const [referralCode, setReferralCode] = useState('');
  const [referralValid, setReferralValid] = useState(null); // null | true | false
  const [referrerName, setReferrerName] = useState('');
  const [checkingCode, setCheckingCode] = useState(false);
  const [pricingCfg, setPricingCfg] = useState(null);

  // Pull live pricing — single source of truth so admin edits propagate.
  useEffect(() => {
    axios.get(`${API}/pricing`).then(r => setPricingCfg(r.data)).catch(() => {});
  }, []);

  const fmt = (n) => `\u20AC${(Number(n) || 0).toFixed(2)}`;
  const monthlyP = pricingCfg?.plans?.monthly;
  const annualP = pricingCfg?.plans?.annual;
  const lifetimeP = pricingCfg?.plans?.lifetime;

  const validateCode = async (code) => {
    if (code.length < 5) { setReferralValid(null); return; }
    setCheckingCode(true);
    try {
      const res = await axios.get(`${API}/stripe/validate-referral/${code}`);
      setReferralValid(res.data.valid);
      setReferrerName(res.data.referrer_name || '');
    } catch {
      setReferralValid(false);
    } finally {
      setCheckingCode(false);
    }
  };

  const handleSelectPlan = (planId) => {
    onSelectPlan(planId, referralValid ? referralCode.toUpperCase() : null);
  };

  // For unregistered users, disable plan buttons until registration fields are valid
  const registrationReady = isAuthenticated || (
    email.trim().length > 0 && password.length >= 6 && password === confirmPassword
  );

  return (
    <div className="space-y-5" data-testid="page-plan-selection">
      {/* Personalised greeting */}
      <div className="text-center">
        <h2 className="text-xl font-bold text-gray-900">
          {displayName
            ? (planPageConfig?.greeting || 'Hey Coach {name}!').replace('{name}', displayName)
            : 'Hey Coach!'}
        </h2>
        <p className="text-sm text-gray-600 mt-2 leading-relaxed"
           dangerouslySetInnerHTML={{ __html: planPageConfig?.trial_text || "We'd like to offer you a <strong>30 day trial for only &pound;1.99</strong> on our annual plan. You can cancel anytime." }}
        />
        {yearsCoaching && (
          <p className="text-sm text-gray-500 mt-2 leading-relaxed"
             dangerouslySetInnerHTML={{ __html: (planPageConfig?.experience_text || "As a coach with around <strong>{years} {unit}</strong> experience, we'd be very interested to hear your feedback during your trial. If at the end of 30 days you decide not to proceed, we'll refund your fee in full.")
               .replace('{years}', yearsCoaching)
               .replace('{unit}', yearsCoaching === 1 ? 'year' : 'years')
             }}
          />
        )}
      </div>

      {/* Registration fields (only for unregistered users) */}
      {!isAuthenticated && (
        <div className="space-y-3 p-4 bg-gray-50 rounded-xl" data-testid="registration-fields">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Create Your Account</p>
          <Input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Email address"
            className="h-11 text-sm rounded-lg"
            autoComplete="email"
            data-testid="register-email-input"
          />
          <Input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Password (min 6 characters)"
            className="h-11 text-sm rounded-lg"
            autoComplete="new-password"
            data-testid="register-password-input"
          />
          <Input
            type="password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            placeholder="Confirm password"
            className={`h-11 text-sm rounded-lg ${
              confirmPassword && password !== confirmPassword ? 'border-red-300' : ''
            }`}
            autoComplete="new-password"
            data-testid="register-confirm-password-input"
          />
          {confirmPassword && password !== confirmPassword && (
            <p className="text-[10px] text-red-500">Passwords do not match</p>
          )}
          <p className="text-[10px] text-gray-400 text-center">
            Already have an account? <a href="/login" className="underline text-emerald-600 hover:text-emerald-700">Log in</a>
          </p>
        </div>
      )}

      {/* Plan cards */}
      <div className="space-y-3">
        {/* Monthly */}
        <button
          onClick={() => handleSelectPlan('monthly')}
          disabled={loading || !registrationReady}
          className={`w-full p-4 rounded-xl border-2 transition-all text-left group ${
            !registrationReady ? 'border-gray-100 opacity-50 cursor-not-allowed' : 'border-gray-200 hover:border-emerald-400'
          }`}
          data-testid="plan-monthly"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold text-gray-900">{monthlyP?.label || 'Monthly'}</p>
              <p className="text-sm text-gray-500">{fmt(monthlyP?.annual_equivalent ?? 107.88)} per year</p>
              {monthlyP?.is_discounted && (
                <p className="text-[10px] text-amber-700 font-bold uppercase tracking-wider mt-0.5" data-testid="plan-monthly-discount-badge">
                  {monthlyP.discount_label || `Save ${Math.round(monthlyP.discount_percent || 0)}%`}
                </p>
              )}
            </div>
            <div className="text-right">
              {monthlyP?.is_discounted && (
                <p className="text-xs text-gray-400 line-through" data-testid="plan-monthly-strike">
                  {fmt(monthlyP.base_amount)}
                </p>
              )}
              <p className="text-2xl font-black text-gray-900" data-testid="plan-monthly-price">
                {fmt(monthlyP?.effective_amount ?? monthlyP?.base_amount ?? 8.99)}
              </p>
              <p className="text-xs text-gray-400">/month</p>
            </div>
          </div>
        </button>

        {/* Annual (recommended) */}
        <button
          onClick={() => handleSelectPlan('annual')}
          disabled={loading || !registrationReady}
          className={`w-full p-4 rounded-xl border-2 bg-emerald-50 transition-all text-left relative overflow-hidden ${
            !registrationReady ? 'border-emerald-200 opacity-50 cursor-not-allowed' : 'border-emerald-500 hover:bg-emerald-100'
          }`}
          data-testid="plan-annual"
        >
          <div className="absolute top-0 right-0 bg-emerald-600 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-bl-lg">
            RECOMMENDED
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold text-gray-900 flex items-center gap-1.5">
                <Star className="w-4 h-4 text-emerald-600" /> {annualP?.label || 'Annual'}
              </p>
              {(() => {
                // Adaptive intro line — paid intro / free trial / no trial
                const tEnabled = annualP?.trial_enabled !== false;
                const tDays = Number(annualP?.trial_days) || 0;
                const tAmt = annualP?.trial_amount;
                const hasPaid = tEnabled && tAmt != null && Number(tAmt) > 0 && tDays > 0;
                const hasFree = tEnabled && tAmt != null && Number(tAmt) === 0 && tDays > 0;
                if (hasPaid) {
                  const eff = annualP?.is_discounted
                    ? (annualP?.effective_amount ?? tAmt)
                    : tAmt;
                  return (
                    <p className="text-sm text-emerald-700 font-medium">
                      {fmt(eff)} for first {tDays} {tDays === 1 ? 'day' : 'days'}
                    </p>
                  );
                }
                if (hasFree) {
                  return (
                    <p className="text-sm text-emerald-700 font-medium">
                      Free {tDays}-day trial
                    </p>
                  );
                }
                return (
                  <p className="text-sm text-emerald-700 font-medium">
                    {fmt(annualP?.effective_amount ?? annualP?.base_amount ?? 83.88)}/year
                  </p>
                );
              })()}
              <p className="text-xs text-gray-500">
                Then {fmt(annualP?.base_amount ?? 83.88)}/year
                {annualP?.monthly_equivalent ? ` (${fmt(annualP.monthly_equivalent)}/mo)` : ''}
              </p>
              {annualP?.is_discounted && (
                <p className="text-[10px] text-amber-700 font-bold uppercase tracking-wider mt-0.5" data-testid="plan-annual-discount-badge">
                  {annualP.discount_label || `Save ${Math.round(annualP.discount_percent || 0)}%`}
                </p>
              )}
              <p className="text-xs text-emerald-600 font-medium mt-0.5">Money back guarantee</p>
            </div>
            <div className="text-right">
              {(() => {
                const tEnabled = annualP?.trial_enabled !== false;
                const tDays = Number(annualP?.trial_days) || 0;
                const tAmt = annualP?.trial_amount;
                const hasPaid = tEnabled && tAmt != null && Number(tAmt) > 0 && tDays > 0;
                const hasFree = tEnabled && tAmt != null && Number(tAmt) === 0 && tDays > 0;
                if (hasFree) {
                  return (
                    <>
                      <p className="text-2xl font-black text-emerald-700" data-testid="plan-annual-price">
                        {fmt(0)}
                      </p>
                      <p className="text-xs text-gray-400">today</p>
                    </>
                  );
                }
                const priceAmt = hasPaid
                  ? (annualP?.is_discounted ? (annualP?.effective_amount ?? tAmt) : tAmt)
                  : (annualP?.effective_amount ?? annualP?.base_amount ?? 83.88);
                return (
                  <>
                    {annualP?.is_discounted && hasPaid && tAmt != null && (
                      <p className="text-xs text-gray-400 line-through" data-testid="plan-annual-strike">
                        {fmt(tAmt)}
                      </p>
                    )}
                    <p className="text-2xl font-black text-emerald-700" data-testid="plan-annual-price">
                      {fmt(priceAmt)}
                    </p>
                    <p className="text-xs text-gray-400">{hasPaid ? 'to start' : 'per year'}</p>
                  </>
                );
              })()}
            </div>
          </div>
        </button>

        {/* Lifetime Deal */}
        <button
          onClick={() => handleSelectPlan('lifetime')}
          disabled={loading || !registrationReady || (plans?.lifetime?.ltd_remaining === 0)}
          className={`w-full p-4 rounded-xl border-2 bg-amber-50 transition-all text-left relative overflow-hidden ${
            !registrationReady ? 'border-amber-200 opacity-50 cursor-not-allowed' : 'border-amber-400 hover:bg-amber-100'
          }`}
          data-testid="plan-lifetime"
        >
          <div className="absolute top-0 right-0 bg-amber-500 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-bl-lg">
            LIMITED
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold text-gray-900 flex items-center gap-1.5">
                <Crown className="w-4 h-4 text-amber-600" /> {lifetimeP?.label || 'Lifetime Deal'}
              </p>
              <p className="text-xs text-gray-500">One-time cost. Limited to first {plans?.lifetime?.ltd_total || 500}.</p>
              <p className="text-xs text-amber-700 font-medium mt-0.5">
                Active while you use the app. Coaches forum. First for updates.
              </p>
              {lifetimeP?.is_discounted && (
                <p className="text-[10px] text-amber-800 font-bold uppercase tracking-wider mt-0.5" data-testid="plan-lifetime-discount-badge">
                  {lifetimeP.discount_label || `Save ${Math.round(lifetimeP.discount_percent || 0)}%`}
                </p>
              )}
            </div>
            <div className="text-right">
              {lifetimeP?.is_discounted && (
                <p className="text-xs text-gray-400 line-through" data-testid="plan-lifetime-strike">
                  {fmt(lifetimeP.base_amount)}
                </p>
              )}
              <p className="text-2xl font-black text-amber-700" data-testid="plan-lifetime-price">
                {fmt(lifetimeP?.effective_amount ?? lifetimeP?.base_amount ?? 59.99)}
              </p>
              <p className="text-xs text-gray-400">one-time</p>
            </div>
          </div>
          {plans?.lifetime?.ltd_remaining != null && (
            <p className="text-[10px] text-amber-600 mt-2 font-medium">
              {plans.lifetime.ltd_remaining} of {plans.lifetime.ltd_total} remaining
            </p>
          )}
        </button>
      </div>

      {/* Currency clarification — EUR is the only billed currency */}
      <p
        className="text-center text-[11px] text-gray-500 font-medium -mt-1"
        data-testid="plan-currency-note"
      >
        {planPageConfig?.currency_note || 'Charged in EUR. See Terms.'}
      </p>

      {/* Cancel anytime */}
      <p className="text-center text-xs text-gray-400 font-medium">CANCEL ANYTIME</p>

      {/* Referral code */}
      <div className="p-3 bg-gray-50 rounded-xl" data-testid="referral-code-section">
        <p className="text-xs font-semibold text-gray-500 mb-1.5">Have a referral code?</p>
        <div className="flex gap-2">
          <Input
            value={referralCode}
            onChange={(e) => {
              const val = e.target.value.toUpperCase();
              setReferralCode(val);
              if (val.length >= 5) validateCode(val);
              else setReferralValid(null);
            }}
            placeholder="e.g. LTD-ABC123"
            className={`h-9 text-sm rounded-lg flex-1 font-mono uppercase ${
              referralValid === true ? 'border-emerald-400 bg-emerald-50' :
              referralValid === false ? 'border-red-300 bg-red-50' : ''
            }`}
            maxLength={10}
            data-testid="referral-code-input"
          />
        </div>
        {checkingCode && <p className="text-[10px] text-gray-400 mt-1">Checking...</p>}
        {referralValid === true && (
          <p className="text-[10px] text-emerald-600 mt-1 flex items-center gap-1" data-testid="referral-valid">
            <Check className="w-3 h-3" /> Referred by Coach {referrerName}
          </p>
        )}
        {referralValid === false && referralCode.length >= 5 && (
          <p className="text-[10px] text-red-500 mt-1" data-testid="referral-invalid">Invalid referral code</p>
        )}
      </div>

      {/* What you get */}
      <div className="p-4 bg-gray-50 rounded-xl">
        <p className="text-sm font-bold text-gray-800 mb-2">{planPageConfig?.features_title || 'What you get on all plans'}</p>
        <p className="text-xs text-gray-500 leading-relaxed">
          {planPageConfig?.features_text || 'Full Premium Access for all Coaches. No catches, no hidden costs, no in-app purchases. No costs to upgrade for extras. You get the whole lot. Just switch off what you don\'t need and keep the app as simple as you need it to suit your coaching needs.'}
        </p>
      </div>

      {/* Loading indicator */}
      {loading && (
        <div className="flex items-center justify-center gap-2 py-2">
          <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">{!isAuthenticated ? 'Creating account & redirecting...' : 'Redirecting to payment...'}</p>
        </div>
      )}

      {/* Terms / Privacy */}
      <p className="text-center text-[11px] text-gray-400">
        By continuing you agree to our{' '}
        <a href="/terms" className="underline hover:text-gray-600">Terms</a> and{' '}
        <a href="/privacy" className="underline hover:text-gray-600">Privacy Policy</a>.
      </p>

      {/* Sign-out escape hatch for users on the wrong account (stale token loop) */}
      {isAuthenticated && onSignOut && (
        <p className="text-center text-[11px] text-gray-400 -mt-3">
          Wrong account?{' '}
          <button
            type="button"
            onClick={onSignOut}
            className="underline text-gray-500 hover:text-gray-700"
            data-testid="plan-signout-btn"
          >
            Sign out
          </button>
        </p>
      )}
    </div>
  );
}
